import React from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { goToManagePlan } from "utils/manage-plan";
import type { UserPermissionsTier } from "utils/entitlements-permission";

export const SECTION_CARD_CLASS =
  "border-t border-neutral-200 bg-white py-4 [&_h3]:mt-0 [&_h3]:mb-1.5 [&_p]:leading-[1.45] [&_.alert]:mt-2 [&_.alert]:mb-3";

export interface PermissionSectionProps {
  /**
   * Section title — rendered as an h3 in both tiers. Required so the locked
   * placeholder has something to show when children are suppressed.
   */
  heading: string;
  /**
   * Supporting copy. In advanced tier it sits under the heading as the
   * section's helper paragraph. In basic tier it's the inline teaser + the
   * hover tooltip body, so it should describe what the feature does, not how
   * to use it. Keep it to one sentence.
   */
  description?: React.ReactNode;
  /**
   * Section body. Only rendered in advanced tier — basic collapses to a
   * heading-only placeholder so the sheet-top upsell stays the single CTA.
   */
  children: React.ReactNode;
  tier: UserPermissionsTier;
  className?: string;
  /** Optional testid to put on the inner section container (advanced tier). */
  dataTestId?: string;
}

/**
 * Wraps a permission control group so it can be collapsed for customers on
 * the basic user-permissions tier. In basic, the whole body is replaced with
 * a one-line teaser + hover tooltip + click-to-upgrade affordance.
 */
export function PermissionSection({
  heading,
  description,
  children,
  tier,
  className,
  dataTestId,
}: PermissionSectionProps): JSX.Element {
  if (tier === "advanced") {
    return (
      <section
        className={cn(SECTION_CARD_CLASS, className)}
        data-testid={dataTestId}
      >
        <h3 className="font-inter text-sm font-semibold text-neutral-900">
          {heading}
        </h3>
        {description && (
          <p className="mb-3 text-sm text-neutral-600">{description}</p>
        )}
        {children}
      </section>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => goToManagePlan()}
          aria-label={`${heading} — available on Accelerate, Enterprise or Custom plans. Click to manage your plan and upgrade.`}
          className={cn(
            "group relative w-full border-t border-neutral-200 bg-white py-4 text-left font-inter transition-colors",
            "hover:bg-blue-50/40",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
            className
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <i
                  className="fa-regular fa-lock text-[11px] text-neutral-400 transition-colors group-hover:text-blue-500"
                  aria-hidden
                />
                {/* Not an <h3> — the button itself already announces the
                    feature via aria-label, and heading landmarks inside
                    interactive controls make screen-reader navigation
                    confusing. */}
                <span className="font-inter text-sm font-semibold text-neutral-600 transition-colors group-hover:text-neutral-900">
                  {heading}
                </span>
              </div>
              {description && (
                <p className="mt-1 text-xs text-neutral-600">{description}</p>
              )}
            </div>
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap transition-colors",
                "bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200",
                "group-hover:bg-blue-600 group-hover:text-white group-hover:ring-blue-600"
              )}
            >
              Accelerate, Enterprise or Custom
            </span>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs" side="left">
        <p className="m-0 mb-1 font-semibold">
          Available on Accelerate, Enterprise or Custom
        </p>
        {description && <p className="m-0 mb-1 opacity-90">{description}</p>}
        <p className="m-0 opacity-90">
          Click to open Manage Plan and change to a plan that includes{" "}
          {heading.toLowerCase()}.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
